import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWhiteLabelling } from 'components/WhiteLabellingProvider';
import useIsViewport from 'hooks/useIsViewport';
import { SupplyDate, Supply } from 'generated/graphql';
import { DAYS_MOBILE, DAYS_DESKTOP, getDayOfWeek } from './product-screens';

export interface ProductDatesResponse {
  startDate: Dayjs;
  selectedDate: Dayjs;
  dateRange: number;
  dates?: SupplyDate[];
  dateIndex: number;
  onStartDateChange: (day: Dayjs) => void;
  screenSizeDays: number;
}

enum DateIndexSwitch {
  Previous = 'Previous',
  Next = 'Next',
}

const useProductDates = ({
  tab,
  supply,
  handleResetPage,
}: {
  tab: number;
  supply?: Supply;
  handleResetPage: () => void;
}): ProductDatesResponse => {
  const isMobile = useIsViewport();
  const dateRange = isMobile ? 5 : 7;
  const { isInputsType } = useWhiteLabelling();
  const screenSizeDays = isInputsType ? 1 : isMobile ? DAYS_MOBILE : DAYS_DESKTOP;

  const [startDate, setStartDate] = useState(() => {
    const todayDate = dayjs();
    const daysFromMonday = getDayOfWeek(todayDate);
    const thisWeekMonday = todayDate.subtract(daysFromMonday, 'day');
    const newStartDate = isInputsType ? thisWeekMonday : todayDate;
    const timezoneOffset = newStartDate.utcOffset();
    return newStartDate.add(timezoneOffset, 'minute');
  });

  const dates = useMemo(() => (tab === 2 ? (supply as Supply)?.marketDates : supply?.dates), [tab, supply]);
  // The previous first date in the date selector on UI
  const [previousActiveDate, setPreviousActiveDate] = useState(startDate);

  const [dateIndex, setDateIndex] = useState(0);
  const [newDateIndex, setNewDateIndex] = useState<DateIndexSwitch | null>(null);
  // The dates shown when a week switch started; the switch settles only once the new week's dates replace them
  const [datesBeforeSwitch, setDatesBeforeSwitch] = useState<SupplyDate[]>();

  const [selectedDate, setSelectedDate] = useState(startDate);

  // Sets the dateIndex to next active supply date before the previous first date in the date selector on UI
  useEffect(() => {
    if (newDateIndex && dates !== datesBeforeSwitch) {
      const previousDateIndex = dates?.findIndex((date) => previousActiveDate.isSame(dayjs(date.csvDate), 'day'));

      if (
        (newDateIndex === DateIndexSwitch.Previous && previousDateIndex === 0) ||
        (newDateIndex === DateIndexSwitch.Next && previousDateIndex !== -1)
      ) {
        return;
      }
      if (newDateIndex === DateIndexSwitch.Previous) {
        setDateIndex((previousDateIndex ?? 1) - 1);
        setSelectedDate(dayjs(dates?.[(previousDateIndex ?? 1) - 1]?.csvDate));
      } else if (newDateIndex === DateIndexSwitch.Next) {
        // Not setting it directly, to prevent UI from doing multiple rerenderings
        setDateIndex(0);
        setSelectedDate(dayjs(dates?.[0]?.csvDate));
      }

      setNewDateIndex(null);
    }
  }, [dates, datesBeforeSwitch, newDateIndex, previousActiveDate]);

  const handleStartDateChange = useCallback(
    (day: Dayjs) => {
      if (!dates || dates.length === 0) {
        return;
      }

      const newDate = dayjs(day);
      const firstDate = dayjs(dates[0].csvDate);
      const lastDate = dayjs(dates[dates.length - 1].csvDate);
      const newIndex = dates.findIndex((date) => dayjs(date.csvDate).isSame(newDate, 'day'));

      setPreviousActiveDate(dayjs(dates[dateIndex]?.csvDate || startDate));

      // New date is before the first date in dates
      if (newDate.isBefore(firstDate, 'day')) {
        const newStartDate = newDate.subtract(7, 'day');
        handleResetPage();
        setStartDate(newStartDate);
        setSelectedDate(newStartDate);
        setNewDateIndex(DateIndexSwitch.Previous);
        setDatesBeforeSwitch(dates);
        return;
      }

      // New date is within the current dates range
      if (newIndex === -1) {
        // If the date isn't found but within range, adjust to closest valid date
        const closestIndex = dates.reduce((prev, curr, idx) => {
          const currDate = dayjs(curr.csvDate);
          return currDate.isAfter(newDate, 'day') ? prev : idx;
        }, 0);
        setDateIndex(closestIndex);
        setSelectedDate(dayjs(dates[closestIndex].csvDate));
      } else if (dates.length - 1 - newIndex < screenSizeDays - 1) {
        setStartDate(newDate);
        setNewDateIndex(DateIndexSwitch.Next);
        setDatesBeforeSwitch(dates);
        handleResetPage();
      } else {
        // Valid index within range
        setDateIndex(newIndex);
        setSelectedDate(dayjs(dates[newIndex].csvDate));
      }
    },
    [dates, screenSizeDays, dateIndex, handleResetPage, startDate],
  );

  return {
    screenSizeDays,
    startDate,
    dates,
    onStartDateChange: handleStartDateChange,
    selectedDate,
    dateRange,
    dateIndex,
  };
};

export default useProductDates;
